import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '@app-environment';
import { combineLatest, filter, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '@shared-lib/foundry-shared/responsive-layout.service';
import { DashboardTitleService } from '@shared-lib/foundry-shared/dashboard-title.service';
import { ScreenNavigationService, ScreenSideNavbarSettings } from '@shared-lib/foundry-shared/screen-navigation.service';
import { RoleAccessService } from '@shared-lib/foundry-shared/role-access.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ThemeService } from '@shared-lib/foundry-shared/theme.service';
import { LayoutService } from '@shared-lib/foundry-shared/layout.service';
import { SlideshowService } from '@shared-lib/foundry-shared/slideshow.service';
import { DockStateService } from '@shared-lib/foundry-shared/dock-state.service';
import { ModuleFlagsService } from '@shared-lib/foundry-shared/module-flags.service';
import { NotificationApiService } from '@shared-lib/foundry-services/notifications/notification-api.service';
import { UserModuleAccessService } from '@shared-lib/foundry-shared/user-module-access.service';
import {
  BusinessAlertListItem,
  BusinessAlertsComponent,
  BusinessAlertsConfig,
} from '../../global-components/business-alerts/business-alerts.component';

export interface SideNavCompareConfig {
  left?: string;
  right?: string;
}

export interface SideNavItem {
  name: string;
  icon?: string;
  path?: string;
  clickable?: boolean;
  moduleKey?: string;
  config_path?: string;
  compare?: SideNavCompareConfig;
  children?: SideNavItem[];
  moduleDisabled?: boolean;
}

interface ThemeModeOption {
  id: 'default' | 'dark';
  label: string;
  icon: string;
  /** When true the option is shown but greyed-out / not selectable. */
  disabled?: boolean;
}

interface AccentOption {
  id: 'blue' | 'navy' | 'red' | 'emerald';
  label: string;
  color: string;
  hoverColor: string;
  /** When true the accent is shown but greyed-out / not selectable. */
  disabled?: boolean;
}

@Component({
  selector: 'app-side-navbar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatTooltipModule,
    RouterModule,
    BusinessAlertsComponent,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent implements AfterViewInit, OnDestroy {
  /** Sidebar drag-resize bounds + persistence key. Min/max chosen so the
   *  menu icons + labels stay readable at min and don't crowd content at max. */
  private static readonly SIDEBAR_MIN_WIDTH = 200;
  private static readonly SIDEBAR_MAX_WIDTH = 480;
  private static readonly SIDEBAR_DEFAULT_WIDTH = 280;
  private static readonly SIDEBAR_WIDTH_STORAGE_KEY = 'dvm.sidebar.expandedWidth';
  private static readonly ACCENT_STORAGE_KEY = 'dvm.theme.accent';

  isExpanded = true;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  isCollapsed = true;
  isMobileLayout = false;
  isMobileDrawerOpen = false;
  username!: string;
  logo!: string;
  companyText = environment.COMPANY_LOGO_TEXT;
  secondaryLogo!: string;
  clientLogo: string = '';
  clientIcon: string = '';
  clientText: string = '';
  hasClientLogo: boolean = false;
  useAltSidenav: boolean = false;

  /** Width (px) used when the sidebar is expanded and not in mobile drawer
   *  mode. Hydrated from localStorage on init; updated live during drag. */
  sidebarExpandedWidth = SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;
  /** True while the user is actively dragging the resize handle. Suppresses
   *  the sidebar's width transition so width tracks the cursor 1:1. */
  isResizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizePointerId: number | null = null;

  // Light/Dark theme toggle
  private themeService = inject(ThemeService);
  private hostElement = inject(ElementRef<HTMLElement>);
  private layoutService = inject(LayoutService);
  private slideshowService = inject(SlideshowService);
  private dockState = inject(DockStateService);
  private moduleFlags = inject(ModuleFlagsService);
  private themeSub?: Subscription;
  private pendingPreviewSub?: Subscription;
  private slideshowStateSub?: Subscription;
  private slideshowProgressSub?: Subscription;
  private dashboardTitleSub?: Subscription;
  private screenNavigationSub?: Subscription;
  private userModuleAccessSub?: Subscription;
  isDarkTheme = false;
  dashboardTitle = '';
  dashboardTitlePosition: 'top' | 'below' | 'default' = 'default';
  dashboardTitleFontSize = '';

  /** Drives the toolbar "Save Theme" button visibility — true after the user
   * hits Apply & Preview in the Theme Options dialog and not yet committed. */
  hasPendingThemePreview = false;

  /** Mirrored slideshow state — toolbar template binds to these for the
   *  inline Play/Pause/Apply/Save/Stop controls. Plain fields rather than
   *  async pipes so multiple template bindings on the same state field
   *  don't open separate subscriptions. */
  slideshowActive = false;
  slideshowPlaying = false;
  slideshowCurrentName = '';
  slideshowIndex = 0;
  slideshowTotal = 0;

  /** 0–1 progress through the current slide cycle. Bound to the progress
   *  bar's width %; updated each animation frame while playing. */
  slideshowProgress = 0;

  /** Live ref to the Theme Options dialog so re-clicking the toolbar button
   * while the dialog is already open is a no-op instead of stacking dialogs. */
  private themeSelectorDialogRef: MatDialogRef<ThemeSelectorComponent> | null = null;
  private hoverNavigationTimer?: ReturnType<typeof setTimeout>;
  private smoothNavigationTimer?: ReturnType<typeof setTimeout>;
  private routeTransitionResetTimer?: ReturnType<typeof setTimeout>;
  private railListenerCleanup: Array<() => void> = [];
  private settingsHoverTimer?: ReturnType<typeof setTimeout>;
  private accountHoverTimer?: ReturnType<typeof setTimeout>;
  private notificationsHoverTimer?: ReturnType<typeof setTimeout>;
  private bgfxAnimationId: number | null = null;
  private bgfxResizeHandler?: () => void;
  private bgfxAlive = false;
  isRouteTransitioning = false;
  dockSide: 'left' | 'right' = 'left';
  isDraggingDock = false;
  previewSide: 'left' | 'right' = 'left';
  shrinkDock = false;
  settingsOpen = false;
  accountOpen = false;
  notificationsOpen = false;
  notificationsLoading = false;
  notificationsError = '';
  popoverPos = { top: '28px', left: '112px' };
  notifications: BusinessAlertListItem[] = [];
  readonly notificationListConfig: BusinessAlertsConfig = {
    title: 'Notifications',
    emptyMessage: 'All caught up',
  };
  // Dark mode is shown but disabled app-wide — only the light theme is selectable.
  themeModeOptions: ThemeModeOption[] = [
    { id: 'default', label: 'Light', icon: 'light_mode' },
    { id: 'dark', label: 'Dark', icon: 'dark_mode', disabled: true },
  ];
  // Only the Blue accent is selectable — the others are shown but disabled.
  accentOptions: AccentOption[] = [
    { id: 'blue', label: 'Blue', color: '#2F8DF4', hoverColor: '#256FE6' },
    { id: 'navy', label: 'Navy', color: '#043D63', hoverColor: '#032E4A', disabled: true },
    { id: 'red', label: 'Red', color: '#7F0000', hoverColor: '#5F0000', disabled: true },
    { id: 'emerald', label: 'Emerald', color: '#2E7D5B', hoverColor: '#1F6B49', disabled: true },
  ];
  selectedAccentId: AccentOption['id'] = this.readStoredAccent();

  @ViewChild('userAvatar') userAvatarEl?: ElementRef<HTMLElement>;

  /** Global dock tooltip — rendered outside the scroll area so it never gets clipped. */
  dockTooltipText = '';
  dockTooltipVisible = false;
  dockTooltipStyle: { top: string; left: string } = { top: '0px', left: '0px' };

  showDockTooltip(event: MouseEvent | FocusEvent, name: string): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const top = `${Math.round(rect.top + rect.height / 2)}px`;
    const left = this.dockSide === 'right'
      ? `${Math.round(rect.left)}px`
      : `${Math.round(rect.right + 12)}px`;
    this.dockTooltipStyle = { top, left };
    this.dockTooltipText = name;
    this.dockTooltipVisible = true;
  }

  hideDockTooltip(): void {
    this.dockTooltipVisible = false;
    this.dockTooltipText = '';
  }

  public get notificationCount(): number {
    return this.notifications.length;
  }

  public toggleTheme(): void {
    // Dark mode is disabled — always resolve to the light theme regardless of
    // which entry point (rail shortcut, keybind) invokes this.
    this.themeService.setTheme('default');
  }

  public isThemeModeSelected(modeId: ThemeModeOption['id']): boolean {
    return modeId === 'dark' ? this.isDarkTheme : !this.isDarkTheme;
  }

  public selectThemeMode(modeId: ThemeModeOption['id']): void {
    // Ignore clicks on disabled modes (e.g. Dark).
    if (this.themeModeOptions.find((mode) => mode.id === modeId)?.disabled) return;
    this.themeService.saveTheme(modeId);
  }

  public selectAccent(accent: AccentOption): void {
    // Ignore clicks on disabled accents (only Blue is selectable).
    if (accent.disabled) return;
    this.selectedAccentId = accent.id;
    try {
      localStorage.setItem(SideNavbarComponent.ACCENT_STORAGE_KEY, accent.id);
    } catch {
      /* ignore */
    }

    const soft = this.withAlpha(accent.color, '12');
    const muted = this.withAlpha(accent.color, '1F');
    const border = this.withAlpha(accent.color, '38');

    this.themeService.saveCustomOverrides({
      layout: {
        sidebar: {
          bg: accent.color,
          text: '#ffffff',
          hoverBg: muted,
          activeBg: accent.hoverColor,
          borderColor: border,
        },
        toolbar: {
          bg: accent.color,
          text: '#ffffff',
          shadow: `0 2px 10px ${this.withAlpha(accent.color, '3D')}`,
        },
        content: {
          headingText: '#0A0A0A',
        },
      },
      card: {
        headerBg: accent.color,
        headerText: '#ffffff',
        borderColor: border,
        hoverShadow: `0 10px 24px ${this.withAlpha(accent.color, '2E')}`,
      },
      chart: {
        palette: [
          accent.color,
          accent.hoverColor,
          '#60A5FA',
          '#2E7D5B',
          '#7F0000',
          '#D97706',
          '#7C3AED',
          '#0891B2',
        ],
        axisLineColor: border,
        splitLineColor: soft,
        legendTextColor: accent.color,
      },
      table: {
        headerBg: accent.color,
        headerText: '#ffffff',
        hoverBg: soft,
        commonBg: soft,
        selectedBg: accent.color,
        selectedText: '#ffffff',
        selectedHoverBg: muted,
        resizeHandleBg: muted,
      },
      filter: {
        iconColor: accent.color,
        optionHoverBg: soft,
      },
      statCard: {
        valueColor: accent.color,
        borderColor: border,
      },
      legend: {
        activeBg: muted,
        hoverBg: soft,
        arrowColor: accent.color,
      },
      dialog: {
        headerText: accent.color,
        confirmBtnBg: accent.color,
      },
      form: {
        focusBorder: accent.color,
      },
      button: {
        primaryBg: accent.color,
        primaryHoverBg: accent.hoverColor,
        secondaryText: accent.color,
      },
      scrollbar: {
        thumbBg: this.withAlpha(accent.color, '66'),
        borderColor: border,
      },
    });
  }

  private withAlpha(hex: string, alpha: string): string {
    return `${hex}${alpha}`;
  }

  private readStoredAccent(): AccentOption['id'] {
    try {
      const saved = localStorage.getItem(SideNavbarComponent.ACCENT_STORAGE_KEY);
      if (saved === 'blue' || saved === 'navy' || saved === 'red' || saved === 'emerald') return saved;
    } catch {
      /* ignore */
    }
    return 'navy';
  }

  /** Persist the previewed theme + customs + layout from the toolbar Save
   *  button. The single Save covers all three pending states so the user
   *  doesn't have to click multiple buttons after previewing changes. */
  saveThemePreview(): void {
    this.themeService.commitPendingPreview();
    this.layoutService.commitPendingLayout();
  }

  // ── Slideshow toolbar actions (delegate to SlideshowService) ─────

  /** Toggle the autoplay timer; the dashboard keeps showing the current
   *  slide either way. */
  toggleSlideshowPlayback(): void {
    if (this.slideshowPlaying) {
      this.slideshowService.pause();
    } else {
      this.slideshowService.play();
    }
  }

  slideshowNext(): void {
    this.slideshowService.next();
  }

  slideshowPrev(): void {
    this.slideshowService.prev();
  }

  /** "Apply & Preview" from the toolbar — keep the current slide as a
   *  pending preview (Save Theme button picks it up), end the slideshow. */
  slideshowApply(): void {
    this.slideshowService.applyAndStop();
  }

  /** Commit current slide to localStorage immediately + end. */
  slideshowSave(): void {
    this.slideshowService.saveAndStop();
  }

  /** Stop and revert to the theme that was active before Play was hit. */
  slideshowStop(): void {
    this.slideshowService.cancelAndStop();
  }

  ngOnDestroy(): void {
    this.clearHoverNavigation();
    this.clearRouteTransitionReset();
    this.clearDockHoverTimers();
    if (this.overflowCloseTimer) clearTimeout(this.overflowCloseTimer);
    this.bgfxAlive = false;
    this.stopBackgroundEffectsCanvas();
    if (this.bgfxResizeHandler) {
      window.removeEventListener('resize', this.bgfxResizeHandler);
      this.bgfxResizeHandler = undefined;
    }
    this.railListenerCleanup.forEach((cleanup) => cleanup());
    this.railListenerCleanup = [];
    this.themeSub?.unsubscribe();
    this.pendingPreviewSub?.unsubscribe();
    this.slideshowStateSub?.unsubscribe();
    this.slideshowProgressSub?.unsubscribe();
    this.dashboardTitleSub?.unsubscribe();
    this.screenNavigationSub?.unsubscribe();
    this.userModuleAccessSub?.unsubscribe();
    // If the user navigates away mid-drag the window listeners would leak
    // and keep firing on a destroyed component — clean them up here.
    if (this.isResizingSidebar) this.endSidebarResize(false);
    if (this.isDraggingDock) this.finishDockDrag(false);
  }

  /** Begin a drag-resize on the sidebar's right edge. No-op on mobile (the
   *  drawer is full-width) or when collapsed (handle isn't rendered). */
  startSidebarResize(event: PointerEvent): void {
    if (this.isMobileLayout || this.isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarExpandedWidth;
    this.isResizingSidebar = true;
    window.addEventListener('pointermove', this.onResizePointerMove);
    window.addEventListener('pointerup', this.onResizePointerUp);
    window.addEventListener('pointercancel', this.onResizePointerUp);
  }

  private onResizePointerMove = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    const delta = event.clientX - this.resizeStartX;
    const next = this.clampSidebarWidth(this.resizeStartWidth + delta);
    if (next !== this.sidebarExpandedWidth) {
      this.sidebarExpandedWidth = next;
    }
  };

  private onResizePointerUp = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    this.endSidebarResize(true);
  };

  /** Tear down the drag — optionally persist the new width and trigger the
   *  same staged reflow used by collapse/expand so chart widgets resize. */
  protected endSidebarResize(commit: boolean): void {
    this.isResizingSidebar = false;
    this.resizePointerId = null;
    window.removeEventListener('pointermove', this.onResizePointerMove);
    window.removeEventListener('pointerup', this.onResizePointerUp);
    window.removeEventListener('pointercancel', this.onResizePointerUp);
    if (commit) {
      this.persistSidebarWidth();
      this.scheduleDesktopReflow();
    }
  }

  protected clampSidebarWidth(width: number): number {
    return Math.min(
      SideNavbarComponent.SIDEBAR_MAX_WIDTH,
      Math.max(SideNavbarComponent.SIDEBAR_MIN_WIDTH, Math.round(width)),
    );
  }

  protected persistSidebarWidth(): void {
    try {
      localStorage.setItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
        String(this.sidebarExpandedWidth),
      );
    } catch {
      /* ignore */
    }
  }

  protected loadSidebarWidth(): void {
    try {
      const raw = localStorage.getItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
      );
      if (!raw) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;
      this.sidebarExpandedWidth = this.clampSidebarWidth(parsed);
    } catch {
      /* ignore */
    }
  }

  /**
   * Select logos based on environment flags.
   * Use Case 1: CLIENT_LOGO_SRC empty → show default SIM Advisory branding (logo + company text).
   * Use Case 2: CLIENT_LOGO_SRC provided → show client logo alone.
   * Colors (toolbar/sidebar bg + text) are controlled by ThemeService via CSS variables.
   */
  protected initLogos(): void {
    const env = environment as any;
    const useAlt = env.USE_ALT_SIDENAV === true || env.USE_ALT_SIDENAV === 'true';
    this.useAltSidenav = useAlt;

    const clientSrc = (env.CLIENT_LOGO_SRC || '').trim();
    const clientIcon = (env.CLIENT_LOGO_ICON || '').trim();
    const clientText = (env.CLIENT_LOGO_TEXT || '').trim();
    this.clientLogo = clientSrc;
    this.clientIcon = clientIcon;
    this.clientText = clientText;
    this.hasClientLogo = !!clientSrc || !!clientIcon;

    if (useAlt) {
      this.logo = env.ALT_COMPANY_LOGO_SRC;
      this.secondaryLogo = env.ALT_SECONDARY_LOGO_SRC;
    } else {
      // Prefer DEFAULT_COMPANY_LOGO (local SVG) over COMPANY_LOGO_SRC (remote PNG) when set.
      const defaultLogo = (env.DEFAULT_COMPANY_LOGO || '').trim();
      this.logo = defaultLogo || environment.COMPANY_LOGO_SRC;
      // Prefer DEFAULT_MAKE_INDIA_LOGO (local SVG) over SECONDARY_LOGO_SRC (remote PNG) when set.
      const defaultMakeIndia = (env.DEFAULT_MAKE_INDIA_LOGO || '').trim();
      this.secondaryLogo = defaultMakeIndia || environment.SECONDARY_LOGO_SRC;
    }
  }

  menuItems: SideNavItem[] = [];
  private allMenuItems: SideNavItem[] = [];
  sideNavVisible = true;
  dockBrandName = '';
  private sideNavbarSettings: ScreenSideNavbarSettings | null = null;
  expandedMenus: Set<string> = new Set();
  isChatRoute: boolean = false;
  /** True on Home. Home has no top navbar, so without this the dock would hug
   *  the very top; we offset it to match the other (top-navbar) pages. */
  isHomeRoute: boolean = false;

  /** How many nav icons the dock shows at rest before the rest are tucked
   *  behind the "more" reveal. */
  private static readonly MENU_VISIBLE_LIMIT = 8;
  /** True while the overflow items (index >= MENU_VISIBLE_LIMIT) are revealed
   *  on hover. Collapses back with a short grace delay so the cursor can
   *  travel from the "more" chip down onto the revealed items. */
  overflowOpen = false;
  private overflowCloseTimer?: ReturnType<typeof setTimeout>;

  /** First N items — always visible. */
  public get primaryMenuItems(): SideNavItem[] {
    return this.menuItems.slice(0, SideNavbarComponent.MENU_VISIBLE_LIMIT);
  }

  /** Items beyond the limit — revealed on hover. */
  public get overflowMenuItems(): SideNavItem[] {
    return this.menuItems.slice(SideNavbarComponent.MENU_VISIBLE_LIMIT);
  }

  public get hasOverflowItems(): boolean {
    return this.menuItems.length > SideNavbarComponent.MENU_VISIBLE_LIMIT;
  }

  public get overflowCount(): number {
    return Math.max(0, this.menuItems.length - SideNavbarComponent.MENU_VISIBLE_LIMIT);
  }

  public openOverflow(): void {
    if (this.overflowCloseTimer) {
      clearTimeout(this.overflowCloseTimer);
      this.overflowCloseTimer = undefined;
    }
    this.overflowOpen = true;
  }

  public scheduleCloseOverflow(): void {
    if (this.overflowCloseTimer) clearTimeout(this.overflowCloseTimer);
    this.overflowCloseTimer = setTimeout(() => {
      this.overflowOpen = false;
      this.overflowCloseTimer = undefined;
    }, 260);
  }
  // Macro panel state
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macros: string[] = [];
  macroErrorMessage: string | null = null;

  public get sidenavWidth(): string {
    if (!this.sideNavVisible) return '0px';
    return this.isMobileLayout ? '96px' : '100px';
  }

  public get contentPadding(): string {
    if (this.viewportContent) return '0';
    if (this.shouldShowWelcomeHeader) return '0';
    return this.isChatRoute ? '0' : '0.5rem';
  }

  public get contentHeight(): string {
    return this.isChatRoute || this.viewportContent ? '100%' : '';
  }

  public get contentMinHeight(): string {
    return this.isChatRoute || this.viewportContent ? '0' : '';
  }

  public get viewportContent(): boolean {
    return this.sideNavbarSettings?.viewportContent === true;
  }

  public get signedInFirstName(): string {
    const source = (this.username || '').trim();
    if (!source) return 'User';
    return source.split(/\s+/)[0] || source;
  }

  public get shouldShowWelcomeHeader(): boolean {
    // Welcome-page sizing must be route-based. Using the first sorted menu
    // item makes whichever screen happens to sort first receive the 1040px
    // home-page max width.
    return this.isHomeRoute;
  }

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
    private responsiveLayoutService: ResponsiveLayoutService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dashboardTitleService: DashboardTitleService,
    private screenNavigationService: ScreenNavigationService,
    private roleAccess: RoleAccessService,
    private notificationApi: NotificationApiService,
    private userModuleAccess: UserModuleAccessService,
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError))
      .subscribe((event: any) => {
        this.hideDockTooltip();
        const url = (event.urlAfterRedirects || event.url || '').split('?')[0];
        this.isChatRoute = url.includes('/chat');
        this.isHomeRoute = url === '/' || url === '' || url.startsWith('/home') || url.startsWith('/spriced-home') || url.startsWith('/s2');
        this.clearRouteTransitionReset();
        this.routeTransitionResetTimer = setTimeout(() => {
          this.isRouteTransitioning = false;
          this.routeTransitionResetTimer = undefined;
        }, 120);
        if (this.isMobileLayout) {
          this.isMobileDrawerOpen = false;
        }
        setTimeout(() => this.logBackgroundEffectsState('router-event'));
      });
  }


  async ngOnInit(): Promise<void> {
    // Seed route flags from the current URL (the NavigationEnd event may have
    // fired before this component mounted, e.g. a hard load into Home).
    const initialUrl = (this.router.url || '').split('?')[0];
    this.isChatRoute = initialUrl.includes('/chat');
    this.isHomeRoute = initialUrl === '/' || initialUrl === '' || initialUrl.startsWith('/home');

    this.breakpointObserver.observe(['(max-width: 991.98px)']).subscribe((state) => {
      this.isMobileLayout = state.matches;
      this.isMobileDrawerOpen = false;
    });

    this.initLogos();
    this.loadSidebarWidth();
    this.loadDockSide();
    // Track active theme so the toolbar toggle icon reflects light/dark state.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.isDarkTheme = this.themeService.currentThemeName === 'dark';
    });
    // Show/hide the toolbar "Save Theme" button based on pending preview
    // state — true if EITHER the theme/custom OR the custom layout is in a
    // previewed-but-not-saved state. A single Save commits whichever is pending.
    this.pendingPreviewSub = combineLatest([
      this.themeService.pendingPreview$,
      this.layoutService.pendingLayout$,
    ]).subscribe(([themePending, layoutPending]) => {
      this.hasPendingThemePreview = themePending || layoutPending;
    });
    // Mirror the slideshow service state into local fields so the toolbar
    // template can show controls (play/pause/prev/next/apply/save/stop) and
    // the progress bar without juggling multiple async pipes — the service
    // is a singleton, so subscribing once here is enough.
    this.slideshowStateSub = this.slideshowService.state$.subscribe((s) => {
      this.slideshowActive = s.active;
      this.slideshowPlaying = s.playing;
      this.slideshowIndex = s.index;
      this.slideshowTotal = s.items.length;
      this.slideshowCurrentName = s.current?.name || '';
    });
    this.slideshowProgressSub = this.slideshowService.progress$.subscribe((p) => {
      this.slideshowProgress = p;
    });
    this.dashboardTitleSub = this.dashboardTitleService.state$.subscribe((state) => {
      this.dashboardTitle = state.title;
      this.dashboardTitlePosition = state.position;
      this.dashboardTitleFontSize = state.fontSize;
    });
    this.screenNavigationSub = this.screenNavigationService.sideNavbar$.subscribe((settings) => {
      this.sideNavbarSettings = settings;
      this.applySideNavbarSettings();
    });
    // Menu permissions come from the access service and token, not the optional
    // profile request. A slow or unavailable profile must not leave the dock empty.
    this.userModuleAccessSub = this.userModuleAccess.allowedModules$.subscribe(() => {
      this.loadSidenavConfig();
    });
    try {
      const isLoggedIn = await this.keycloakService.isLoggedIn();
      if (isLoggedIn) {
        const userDetails = await this.keycloakService.loadUserProfile();
        this.username =
          `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim() || 'user';
        const email = String(
          (this.keycloakService.getKeycloakInstance().tokenParsed as { email?: string } | undefined)?.email || '',
        ).trim();
        this.loadNotifications(email);
      }
    } catch {
      this.username = 'user';
      console.warn('Sidebar user profile could not be loaded.');
    }
  }

  private loadDockSide(): void {
    try {
      const savedSide = localStorage.getItem('dock_side');
      if (savedSide === 'right') {
        this.dockSide = 'right';
      }
    } catch {
      /* ignore */
    }
    this.previewSide = this.dockSide;
    this.dockState.setSide(this.dockSide);
  }

  public startDockDrag(event: MouseEvent): void {
    if (this.isMobileLayout) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingDock = true;
    this.previewSide = this.dockSide;
    this.shrinkDock = false;
    document.body.style.cursor = 'grabbing';
  }

  @HostListener('document:mousemove', ['$event'])
  onGlobalMouseMove(event: MouseEvent): void {
    if (!this.isDraggingDock) return;
    this.previewSide = event.clientX > window.innerWidth / 2 ? 'right' : 'left';
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.dockTooltipVisible) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.futuristic-dock')) return;
    this.hideDockTooltip();
  }

  @HostListener('document:mouseup')
  onGlobalMouseUp(): void {
    if (!this.isDraggingDock) return;
    this.finishDockDrag(true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.settingsOpen = false;
    this.accountOpen = false;
    this.notificationsOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.settingsOpen = false;
    this.accountOpen = false;
    this.notificationsOpen = false;
  }

  private finishDockDrag(commit: boolean): void {
    this.isDraggingDock = false;
    document.body.style.cursor = '';
    if (!commit) return;

    this.dockSide = this.previewSide;
    try {
      localStorage.setItem('dock_side', this.dockSide);
    } catch {
      /* ignore */
    }
    this.dockState.setSide(this.dockSide);
    this.scheduleDesktopReflow();
  }

  private clearDockHoverTimers(): void {
    if (this.settingsHoverTimer) clearTimeout(this.settingsHoverTimer);
    if (this.accountHoverTimer) clearTimeout(this.accountHoverTimer);
    if (this.notificationsHoverTimer) clearTimeout(this.notificationsHoverTimer);
  }

  public openSettingsHover(): void {
    if (this.settingsHoverTimer) clearTimeout(this.settingsHoverTimer);
    this.notificationsOpen = false;
    this.accountOpen = false;
    this.settingsOpen = true;
  }

  public closeSettingsHover(): void {
    this.settingsHoverTimer = setTimeout(() => {
      this.settingsOpen = false;
    }, 160);
  }

  public openAccountCardHover(): void {
    if (this.accountHoverTimer) clearTimeout(this.accountHoverTimer);
    const rect = this.userAvatarEl?.nativeElement.getBoundingClientRect();
    if (rect) {
      const cardWidth = 260;
      const cardHeight = 190;
      const gap = 14;
      const top = Math.max(
        16,
        Math.min(Math.round(rect.bottom - cardHeight), window.innerHeight - cardHeight - 16),
      );
      this.popoverPos = this.dockSide === 'left'
        ? { top: `${top}px`, left: `${Math.round(rect.right + gap)}px` }
        : { top: `${top}px`, left: `${Math.round(rect.left - cardWidth - gap)}px` };
    }
    this.settingsOpen = false;
    this.notificationsOpen = false;
    this.accountOpen = true;
  }

  public closeAccountCardHover(): void {
    this.accountHoverTimer = setTimeout(() => {
      this.accountOpen = false;
    }, 160);
  }

  public openNotificationsHover(): void {
    if (this.notificationsHoverTimer) clearTimeout(this.notificationsHoverTimer);
    this.settingsOpen = false;
    this.accountOpen = false;
    this.notificationsOpen = true;
  }

  public closeNotificationsHover(): void {
    this.notificationsHoverTimer = setTimeout(() => {
      this.notificationsOpen = false;
    }, 160);
  }

  public clearNotifications(): void {
    this.notifications = [];
  }

  private loadNotifications(email: string): void {
    if (!email) {
      this.notificationsError = 'Unable to identify the signed-in user.';
      return;
    }
    this.notificationsLoading = true;
    this.notificationsError = '';
    this.notificationApi.getNotifications(email).subscribe({
      next: (response) => {
        const events = Array.isArray(response?.events) ? response.events : [];
        const apiNotifications: BusinessAlertListItem[] = events
          .filter((event) => event && (typeof event.notification_data === 'string' || event.file?.file_data))
          .map((event): BusinessAlertListItem => ({
            id: event.event_id,
            message: typeof event.notification_data === 'string'
              ? event.notification_data
              : 'A notification file is available for download.',
            filterCondition: typeof event.filter_condition === 'string' && event.filter_condition.trim()
              ? event.filter_condition.trim()
              : undefined,
            severity: this.notificationSeverity(event.notification_data),
            download: event.file?.file_data
              ? {
                fileName: event.file.file_name || 'notification-download',
                fileType: event.file.file_type || 'application/octet-stream',
                fileData: event.file.file_data,
              }
              : undefined,
          }));
        this.notifications = apiNotifications;
        this.notificationsLoading = false;
      },
      error: () => {
        this.notifications = [];
        this.notificationsLoading = false;
        this.notificationsError = 'Notifications could not be loaded.';
      },
    });
  }

  private notificationSeverity(message: unknown): BusinessAlertListItem['severity'] {
    const value = String(message || '').toLowerCase();
    if (['pending', 'failed', 'failure', 'error', 'denied', 'rejected'].some((keyword) => value.includes(keyword))) {
      return 'error';
    }
    if (['saved', 'success', 'successful', 'completed', 'approved'].some((keyword) => value.includes(keyword))) {
      return 'success';
    }
    return 'info';
  }

  public downloadNotificationFile(notification: BusinessAlertListItem): void {
    const file = notification.download;
    if (!file?.fileData) return;

    try {
      const dataUrlMatch = file.fileData.match(/^data:([^;,]+)?;base64,/i);
      const base64 = (dataUrlMatch ? file.fileData.slice(dataUrlMatch[0].length) : file.fileData)
        .replace(/\s/g, '');
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      const blob = new Blob([bytes], { type: dataUrlMatch?.[1] || file.fileType || 'application/octet-stream' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = this.sanitizeNotificationFileName(file.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.notificationsError = 'The notification file could not be downloaded.';
    }
  }

  private sanitizeNotificationFileName(fileName: string): string {
    return fileName.trim().split(/[\\/]/).pop()?.replace(/[\r\n"]/g, '').trim() || 'notification-download';
  }

  ngAfterViewInit(): void {
    this.bindRailEvents();
    this.bgfxAlive = true;
    this.bgfxResizeHandler = () => this.restartBackgroundEffectsCanvas();
    window.addEventListener('resize', this.bgfxResizeHandler);
    this.restartBackgroundEffectsCanvas();
    setTimeout(() => this.logBackgroundEffectsState('after-view-init'));
  }

  private logBackgroundEffectsState(stage: string): void {
    const root = this.hostElement.nativeElement.querySelector('.nav-bar') as HTMLElement | null;
    const content = this.hostElement.nativeElement.querySelector('.app-content') as HTMLElement | null;
    const canvas = this.hostElement.nativeElement.querySelector('.bgfx-orchestra') as HTMLCanvasElement | null;
    const rootRect = root?.getBoundingClientRect();
    const contentRect = content?.getBoundingClientRect();
    const canvasRect = canvas?.getBoundingClientRect();
    console.info('[DVM-BGFX-20260626] side-navbar-state', {
      stage,
      bodyHasClass: document.body.classList.contains('dashboard-background-effects-active'),
      htmlHasClass: document.documentElement.classList.contains('dashboard-background-effects-active'),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      canvasRect: canvasRect ? {
        left: Math.round(canvasRect.left),
        top: Math.round(canvasRect.top),
        width: Math.round(canvasRect.width),
        height: Math.round(canvasRect.height),
      } : null,
      navBarRect: rootRect ? {
        left: Math.round(rootRect.left),
        top: Math.round(rootRect.top),
        width: Math.round(rootRect.width),
        height: Math.round(rootRect.height),
      } : null,
      appContentRect: contentRect ? {
        left: Math.round(contentRect.left),
        top: Math.round(contentRect.top),
        width: Math.round(contentRect.width),
        height: Math.round(contentRect.height),
      } : null,
    });
  }

  private restartBackgroundEffectsCanvas(): void {
    this.stopBackgroundEffectsCanvas();
    const canvas = this.hostElement.nativeElement.querySelector('.bgfx-orchestra') as HTMLCanvasElement | null;
    if (!canvas) return;
    this.runBackgroundEffectsCanvas(canvas, '34,50,86');
  }

  private stopBackgroundEffectsCanvas(): void {
    if (this.bgfxAnimationId !== null) {
      cancelAnimationFrame(this.bgfxAnimationId);
      this.bgfxAnimationId = null;
    }
  }

  private runBackgroundEffectsCanvas(canvas: HTMLCanvasElement, accent: string): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, window.innerWidth);
    const h = Math.max(1, window.innerHeight);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = w / 2;
    const cy = h * 0.5;
    const fadeR = Math.min(w, h) * 0.5;
    const fade = (x: number, y: number) => Math.max(0.2, Math.min(1, Math.hypot(x - cx, y - cy) / fadeR));
    const roles = ['transform', 'retrieve', 'persist', 'translate', 'integrate', 'router', 'validator', 'transient', 'scheduler'];
    const palette: Record<string, string> = { dispatch: accent, result: '95,118,165', handoff: '132,151,180' };
    const agentCount = Math.min(30, Math.max(16, Math.round(w * h / 52000)));
    const agents: any[] = [];
    let tries = 0;

    while (agents.length < agentCount && tries < agentCount * 70) {
      tries++;
      const x = 44 + Math.random() * (w - 88);
      const y = 44 + Math.random() * (h - 88);
      if (agents.some((other) => Math.hypot(other.x - x, other.y - y) < 76)) continue;
      agents.push({
        x,
        y,
        bx: x,
        by: y,
        ph: Math.random() * 6.28,
        amp: 7 + Math.random() * 11,
        role: roles[(Math.random() * roles.length) | 0],
        hub: false,
        busy: 0,
        work: 1,
        pop: null,
        r: 2.4,
      });
    }

    const hubCount = Math.max(2, Math.round(agents.length / 8));
    const hubIdx: number[] = [];
    while (hubIdx.length < hubCount && agents.length) {
      const i = (Math.random() * agents.length) | 0;
      if (!hubIdx.includes(i)) hubIdx.push(i);
    }
    hubIdx.forEach((i) => {
      agents[i].hub = true;
      agents[i].role = 'oopschestrator';
      agents[i].r = 4.2;
    });
    const hubs = hubIdx.map((i) => agents[i]);
    agents.forEach((agent) => {
      if (agent.hub || !hubs.length) return;
      let best = hubs[0];
      let bestDistance = Number.POSITIVE_INFINITY;
      hubs.forEach((hub) => {
        const distance = Math.hypot(agent.x - hub.x, agent.y - hub.y);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = hub;
        }
      });
      agent.parent = best;
    });

    const hubEdges: any[] = [];
    for (let i = 0; i < hubs.length; i++) {
      for (let j = i + 1; j < hubs.length; j++) {
        hubEdges.push([hubs[i], hubs[j]]);
      }
    }

    const pulses: any[] = [];
    const ripples: any[] = [];
    let frame = 0;

    const dispatch = () => {
      if (!hubs.length) return;
      const hub = hubs[(Math.random() * hubs.length) | 0];
      const kids = agents.filter((agent) => agent.parent === hub && agent.busy <= 0);
      if (!kids.length) return;
      const target = kids[(Math.random() * kids.length) | 0];
      pulses.push({
        from: hub,
        to: target,
        p: 0,
        kind: 'dispatch',
        speed: 0.017 + Math.random() * 0.011,
        onArrive: () => {
          target.busy = 64 + Math.random() * 70;
          target.work = target.busy;
          target.pop = { txt: 'working...', a: 1 };
        },
      });
    };

    const draw = (time: number) => {
      frame++;
      ctx.clearRect(0, 0, w, h);
      agents.forEach((agent) => {
        agent.x = agent.bx + Math.cos(time * 0.0004 + agent.ph) * agent.amp;
        agent.y = agent.by + Math.sin(time * 0.0005 + agent.ph) * agent.amp;
      });

      ctx.lineWidth = 1;
      agents.forEach((agent) => {
        if (agent.hub || !agent.parent) return;
        const f = fade((agent.x + agent.parent.x) / 2, (agent.y + agent.parent.y) / 2);
        ctx.strokeStyle = `rgba(${accent},${0.13 * f})`;
        ctx.beginPath();
        ctx.moveTo(agent.parent.x, agent.parent.y);
        ctx.lineTo(agent.x, agent.y);
        ctx.stroke();
      });

      ctx.setLineDash([4, 6]);
      hubEdges.forEach(([p, q]) => {
        const f = fade((p.x + q.x) / 2, (p.y + q.y) / 2);
        ctx.strokeStyle = `rgba(${palette['handoff']},${0.16 * f})`;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(q.x, q.y);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      if (frame % 30 === 0) dispatch();

      agents.forEach((agent) => {
        if (agent.busy <= 0) return;
        agent.busy--;
        if (agent.busy > 0) return;
        const hub = agent.parent || hubs[0];
        pulses.push({
          from: agent,
          to: hub,
          p: 0,
          kind: 'result',
          speed: 0.02 + Math.random() * 0.01,
          onArrive: () => ripples.push({ x: hub.x, y: hub.y, r: 6, a: 0.85 }),
        });
        agent.pop = { txt: 'result ok', a: 1 };
        if (Math.random() < 0.5) {
          const others = agents.filter((other) => other !== agent && !other.hub && other.busy <= 0);
          if (others.length) {
            const other = others[(Math.random() * others.length) | 0];
            pulses.push({
              from: agent,
              to: other,
              p: 0,
              kind: 'handoff',
              speed: 0.016,
              onArrive: () => {
                other.busy = 52 + Math.random() * 60;
                other.work = other.busy;
                other.pop = { txt: 'handoff', a: 1 };
              },
            });
          }
        }
      });

      for (let k = pulses.length - 1; k >= 0; k--) {
        const pulse = pulses[k];
        pulse.p += pulse.speed;
        const x = pulse.from.x + (pulse.to.x - pulse.from.x) * pulse.p;
        const y = pulse.from.y + (pulse.to.y - pulse.from.y) * pulse.p;
        if (pulse.p >= 1) {
          if (pulse.onArrive) pulse.onArrive();
          pulses.splice(k, 1);
          continue;
        }
        const f = fade(x, y);
        const col = palette[pulse.kind];
        const tp = Math.max(0, pulse.p - 0.07);
        const tx = pulse.from.x + (pulse.to.x - pulse.from.x) * tp;
        const ty = pulse.from.y + (pulse.to.y - pulse.from.y) * tp;
        const gradient = ctx.createLinearGradient(tx, ty, x, y);
        gradient.addColorStop(0, `rgba(${col},0)`);
        gradient.addColorStop(1, `rgba(${col},${0.62 * f})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.fillStyle = `rgba(${col},${0.78 * f})`;
        ctx.beginPath();
        ctx.arc(x, y, 2.6, 0, 7);
        ctx.fill();
      }

      for (let k = ripples.length - 1; k >= 0; k--) {
        const ripple = ripples[k];
        ripple.r += 1.5;
        ripple.a *= 0.94;
        if (ripple.a < 0.03) {
          ripples.splice(k, 1);
          continue;
        }
        const f = fade(ripple.x, ripple.y);
        ctx.strokeStyle = `rgba(${palette['result']},${ripple.a * f})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.r, 0, 7);
        ctx.stroke();
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      agents.forEach((agent) => {
        const f = fade(agent.x, agent.y);
        const pulse = agent.busy > 0 ? (0.5 + 0.5 * Math.sin(frame * 0.16)) : 0;
        const progress = agent.busy > 0 ? (1 - agent.busy / agent.work) : 0;
        const glowRadius = agent.hub ? agent.r * 5.2 : agent.r * 4.2;
        ctx.fillStyle = `rgba(${accent},${((agent.hub ? 0.08 : 0.04) + pulse * 0.1) * f})`;
        ctx.beginPath();
        ctx.arc(agent.x, agent.y, glowRadius * (1 + pulse * 0.3), 0, 7);
        ctx.fill();

        if (agent.hub) {
          const radius = agent.r * 2.6;
          ctx.fillStyle = `rgba(255,255,255,${0.58 * f})`;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, radius, 0, 7);
          ctx.fill();
          ctx.strokeStyle = `rgba(${accent},${0.58 * f})`;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, radius, 0, 7);
          ctx.stroke();
          ctx.strokeStyle = `rgba(${accent},${0.24 * f})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, radius - 4, 0, 7);
          ctx.stroke();
          for (let s = 0; s < 12; s++) {
            const angle = frame * 0.01 + s * Math.PI / 6;
            const major = s % 3 === 0;
            const r1 = radius + 3;
            const r2 = radius + (major ? 9 : 6);
            ctx.strokeStyle = `rgba(${accent},${(major ? 0.42 : 0.22) * f})`;
            ctx.lineWidth = major ? 1.4 : 1;
            ctx.beginPath();
            ctx.moveTo(agent.x + Math.cos(angle) * r1, agent.y + Math.sin(angle) * r1);
            ctx.lineTo(agent.x + Math.cos(angle) * r2, agent.y + Math.sin(angle) * r2);
            ctx.stroke();
          }
          ctx.fillStyle = `rgba(82,96,124,${0.66 * f})`;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, agent.r * 1.1, 0, 7);
          ctx.fill();
        } else {
          const radius = agent.r * 2.2;
          ctx.fillStyle = `rgba(255,255,255,${0.5 * f})`;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, radius, 0, 7);
          ctx.fill();
          ctx.strokeStyle = `rgba(${accent},${0.28 * f})`;
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, radius, 0, 7);
          ctx.stroke();
          if (agent.busy > 0) {
            ctx.strokeStyle = `rgba(${palette['result']},${0.7 * f})`;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.arc(agent.x, agent.y, radius, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
            ctx.stroke();
            ctx.lineCap = 'butt';
          }
          ctx.fillStyle = `rgba(${agent.busy > 0 ? palette['result'] : accent},${(0.52 + pulse * 0.24) * f})`;
          ctx.beginPath();
          ctx.arc(agent.x, agent.y, agent.r * (agent.busy > 0 ? 1.0 : 0.82), 0, 7);
          ctx.fill();
        }

        ctx.font = `${agent.hub ? '600 12px' : '500 10.5px'} "IBM Plex Mono", monospace`;
        const labelAlpha = (agent.hub ? 0.46 : (0.24 + pulse * 0.32)) * f;
        ctx.fillStyle = `rgba(82,94,116,${labelAlpha})`;
        ctx.fillText(agent.role, agent.x, agent.y + (agent.hub ? agent.r * 3.2 + 12 : agent.r * 2.4 + 9));

        if (agent.pop) {
          agent.pop.a *= 0.972;
          if (agent.pop.a < 0.05) {
            agent.pop = null;
          } else {
            ctx.font = '500 11px "IBM Plex Mono", monospace';
            const cp = agent.pop.txt.includes('ok') ? palette['result'] : (agent.pop.txt === 'handoff' ? palette['handoff'] : accent);
            ctx.fillStyle = `rgba(${cp},${agent.pop.a * 0.72 * f})`;
            ctx.fillText(agent.pop.txt, agent.x, agent.y - agent.r * 2.4 - 10);
          }
        }
      });
    };

    const tick = (time: number) => {
      if (!this.bgfxAlive) return;
      draw(time);
      this.bgfxAnimationId = requestAnimationFrame(tick);
    };
    this.bgfxAnimationId = requestAnimationFrame(tick);
    console.info('[DVM-BGFX-20260626] canvas-start', { width: w, height: h, agentCount, accent });
  }

  private bindRailEvents(): void {
    const railHost = this.hostElement.nativeElement;

    const listeners: Array<[string, EventListener]> = [
      ['pointerover', (event) => this.handleRailEnter(event)],
      ['focusin', (event) => this.handleRailEnter(event)],
      ['pointerout', (event) => this.handleRailLeave(event)],
      ['focusout', (event) => this.handleRailLeave(event)],
      ['click', (event) => this.handleRailClick(event)],
    ];

    listeners.forEach(([name, listener]) => {
      railHost.addEventListener(name, listener);
      this.railListenerCleanup.push(() => railHost.removeEventListener(name, listener));
    });
  }

  private handleRailEnter(event: Event): void {
    const target = event.target as HTMLElement | null;
    const navButton = target?.closest<HTMLElement>('[data-nav-index]');
    if (!navButton) return;

    const item = this.getRailItemFromElement(navButton);
    if (!item) return;

    if (navButton.classList.contains('rail-subitem')) {
      this.scheduleHoverNavigation(item);
    } else if (this.hasChildren(item)) {
      this.openMenuOnHover(item);
    } else {
      this.onRailItemHover(item);
    }
  }

  private handleRailLeave(event: Event): void {
    const target = event.target as HTMLElement | null;
    const navButton = target?.closest<HTMLElement>('[data-nav-index]');
    if (!navButton) return;

    const relatedTarget = (event as PointerEvent | FocusEvent).relatedTarget as Node | null;
    if (relatedTarget && navButton.contains(relatedTarget)) return;

    this.clearHoverNavigation();
  }

  private handleRailClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const actionButton = target?.closest<HTMLElement>('[data-rail-action]');
    if (actionButton) {
      event.preventDefault();
      this.handleRailAction(actionButton.dataset['railAction'] || '');
      return;
    }

    const navButton = target?.closest<HTMLElement>('[data-nav-index]');
    if (!navButton) return;

    event.preventDefault();
    const item = this.getRailItemFromElement(navButton);
    if (!item) return;

    if (!navButton.classList.contains('rail-subitem') && this.hasChildren(item)) {
      this.toggleSubmenu(item.name, event);
      return;
    }

    this.navigateToMenuItem(item);
  }

  private handleRailAction(action: string): void {
    if (action === 'toggle-sidebar') {
      this.toggleSidebar();
      return;
    }
    if (action === 'toggle-theme') {
      this.toggleTheme();
      return;
    }
    if (action === 'logout') {
      void this.logout();
    }
  }

  private getRailItemFromElement(element: HTMLElement): SideNavItem | null {
    const itemIndex = Number(element.dataset['navIndex']);
    if (!Number.isInteger(itemIndex)) return null;

    const item = this.menuItems[itemIndex];
    if (!item) return null;

    const childIndexValue = element.dataset['childIndex'];
    if (childIndexValue === undefined) return item;

    const childIndex = Number(childIndexValue);
    if (!Number.isInteger(childIndex)) return null;

    return this.getMenuChildren(item)[childIndex] || null;
  }

  public async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  public loadSidenavConfig(): void {
    this.http
      .get<SideNavItem[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe(async (data) => {
        const items = Array.isArray(data) ? data : [];
        await Promise.all([this.moduleFlags.load(), this.roleAccess.load()]);
        let tokenRoles: string[] = [];
        try {
          tokenRoles = this.keycloakService.getUserRoles(true) || [];
        } catch {
          tokenRoles = [];
        }
        const email = String((this.keycloakService.getKeycloakInstance()?.tokenParsed as any)?.email || '');
        const roles = this.roleAccess.resolveRoles(email, tokenRoles);
        const moduleKeyOf = (item: SideNavItem) => item.moduleKey ?? item.path?.replace(/^\//, '');
        this.allMenuItems = items
          .map((item) => this.filterMenuItemByUserModules(item))
          .filter((item): item is SideNavItem => !!item)
          .filter((item) => this.moduleFlags.shouldShowInNavigation(moduleKeyOf(item)))
          .map((item) => ({
            ...item,
            moduleDisabled: !this.moduleFlags.isActive(moduleKeyOf(item)),
          }))
          .filter((item) => this.canAccessMenuItem(item, roles))
          // modules.config.json is the single source of truth for menu order.
          .sort((a, b) => this.moduleFlags.compareOrder(moduleKeyOf(a), moduleKeyOf(b)));
        this.applySideNavbarSettings();
      });
  }

  private filterMenuItemByUserModules(item: SideNavItem): SideNavItem | null {
    const children = (item.children || [])
      .map((child) => this.filterMenuItemByUserModules(child))
      .filter((child): child is SideNavItem => !!child);
    const navigationKey = this.normalizeMenuSetting(
      item.moduleKey || item.path || '',
    );
    const alwaysVisible = new Set(['home-main', 'spriced-home']);
    const permissionKeyByNavigationKey: Record<string, string> = {
      'domain-catalogs': 'item-catalogs',
      'approval-workflows': 'approvals',
      'chain-list': 'approvals',
      'ttm-list': 'approvals',
      'ttm': 'approvals',
      'audit': 'approvals',
      'deam-service': 'approvals',
    };
    const permissionKey = permissionKeyByNavigationKey[navigationKey] || navigationKey;
    const permitted = alwaysVisible.has(navigationKey) || this.userModuleAccess.canAccess(permissionKey);

    if (children.length) return { ...item, children };
    return permitted ? { ...item, children: undefined } : null;
  }

  private applySideNavbarSettings(): void {
    const settings = this.sideNavbarSettings;
    this.dockBrandName = this.resolveDockBrandName(settings);
    this.sideNavVisible = !(
      settings?.enabled === false ||
      settings?.visible === false ||
      settings?.hidden === true
    );

    if (!this.sideNavVisible) {
      this.menuItems = [];
      this.expandedMenus.clear();
      this.overflowOpen = false;
      return;
    }

    this.menuItems = this.filterMenuItemsForSettings(this.allMenuItems, settings);
  }

  private resolveDockBrandName(settings: ScreenSideNavbarSettings | null): string {
    if (!settings) return 'SIM';

    const brandKeys = [
      'brandName',
      'brandLabel',
      'dockBrandName',
      'dockBrandLabel',
      'logoText',
      'title',
      'name',
    ];

    for (const key of brandKeys) {
      if (!Object.prototype.hasOwnProperty.call(settings, key)) continue;
      const raw = (settings as any)[key];
      return typeof raw === 'string' ? raw.trim() : '';
    }

    return 'SIM';
  }

  private filterMenuItemsForSettings(items: SideNavItem[], settings: ScreenSideNavbarSettings | null): SideNavItem[] {
    if (!settings) return [...items];

    const include = this.toSettingSet(
      this.moduleFlags.resolveNavigationIncludes(settings.include ?? []),
      settings.items,
      settings.visibleItems,
      settings.moduleKeys,
      settings.paths,
      settings.names,
    );
    const exclude = this.toSettingSet(settings.exclude, settings.hiddenItems, settings.excludedItems);

    if (!include.size && !exclude.size) return [...items];

    return items
      .map((item) => this.filterMenuItem(item, include, exclude, false))
      .filter((item): item is SideNavItem => !!item);
  }

  private filterMenuItem(
    item: SideNavItem,
    include: Set<string>,
    exclude: Set<string>,
    parentIncluded: boolean,
  ): SideNavItem | null {
    if (exclude.size && this.matchesMenuSetting(item, exclude)) return null;

    const selfIncluded = !include.size || parentIncluded || this.matchesMenuSetting(item, include);
    const children = this.getMenuChildren(item)
      .map((child) => this.filterMenuItem(child, include, exclude, selfIncluded))
      .filter((child): child is SideNavItem => !!child);

    if (children.length) return { ...item, children };
    return selfIncluded ? { ...item, children: undefined } : null;
  }

  private toSettingSet(...groups: Array<string[] | undefined>): Set<string> {
    const values = groups
      .flatMap((group) => Array.isArray(group) ? group : [])
      .map((value) => this.normalizeMenuSetting(value))
      .filter((value) => !!value);
    return new Set(values);
  }

  private matchesMenuSetting(item: SideNavItem, settings: Set<string>): boolean {
    const path = String(item.path || '').trim();
    const configPath = String(item.config_path || '').trim();
    const candidates = [
      item.name,
      item.moduleKey,
      path,
      path.replace(/^\//, ''),
      configPath,
      configPath.split('/').filter(Boolean).pop(),
      configPath.split('/').filter(Boolean).pop()?.replace(/\.json$/i, ''),
    ];
    return candidates.some((candidate) => settings.has(this.normalizeMenuSetting(candidate)));
  }

  private normalizeMenuSetting(value: unknown): string {
    return String(value ?? '').trim().replace(/^\//, '').toLowerCase();
  }

  /** Whether the current user's roles allow this nav item (and prunes children). */
  private canAccessMenuItem(item: SideNavItem, roles: string[]): boolean {
    const path = (item.path || '').replace(/^\//, '');
    if (item.children?.length) {
      item.children = item.children.filter((child) => this.canAccessMenuItem(child, roles));
      if (item.children.length) return true;
      return path ? this.roleAccess.canAccess(path, roles) : false;
    }
    // Items without a route (section headers) always show.
    if (!path) return true;
    return this.roleAccess.canAccess(path, roles);
  }

  public onRailItemHover(item: SideNavItem): void {
    if (!item) return;
    this.expandedMenus.clear();
    this.scheduleHoverNavigation(item);
  }

  public openMenuOnHover(item: SideNavItem): void {
    if (!item?.name) return;
    this.expandedMenus.clear();
    this.expandedMenus.add(item.name);
    if (item.path) {
      this.scheduleHoverNavigation(item);
    }
  }

  /**
   * Hover-to-navigate, debounced. The debounce is the key to a glitch-free
   * feel: every new hover cancels the previous pending navigation, so sliding
   * the cursor across intermediate items on the way to the intended one never
   * routes through their pages — only the item you actually pause on (for
   * ~150ms) navigates. We deliberately do NOT toggle `isRouteTransitioning`
   * here; dipping the content opacity on every hover was itself read as a
   * flicker.
   */
  public scheduleHoverNavigation(item: SideNavItem): void {
    if (this.isMobileLayout || !item?.path || item.moduleDisabled || this.isMenuItemClickOnly(item) || this.isSameMenuTarget(item)) return;

    this.clearHoverNavigation();
    this.hoverNavigationTimer = setTimeout(() => {
      this.hoverNavigationTimer = undefined;
      this.navigateToMenuItem(item);
    }, 260);
  }

  public clearHoverNavigation(): void {
    if (this.hoverNavigationTimer) {
      clearTimeout(this.hoverNavigationTimer);
      this.hoverNavigationTimer = undefined;
    }
    if (this.smoothNavigationTimer) {
      clearTimeout(this.smoothNavigationTimer);
      this.smoothNavigationTimer = undefined;
      this.isRouteTransitioning = false;
    }
  }

  private clearRouteTransitionReset(): void {
    if (this.routeTransitionResetTimer) {
      clearTimeout(this.routeTransitionResetTimer);
      this.routeTransitionResetTimer = undefined;
    }
  }

  public navigateToMenuItem(item: SideNavItem): void {
    if (!item?.path || item.moduleDisabled) return;
    this.clearHoverNavigation();
    this.hideDockTooltip();

    const queryParams: Record<string, string> = {};
    if (item.config_path) {
      queryParams['config_path'] = item.config_path;
    }
    if (item.compare?.left || item.compare?.right) {
      if (item.compare.left) queryParams['left'] = item.compare.left;
      if (item.compare.right) queryParams['right'] = item.compare.right;
    }

    this.router.navigate([item.path], {
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
    });

    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = false;
    }
  }

  public isSameMenuTarget(item: SideNavItem): boolean {
    if (!item?.path) return true;
    const targetPath = String(item.path).startsWith('/') ? String(item.path) : `/${item.path}`;
    if (this.router.parseUrl(this.router.url).root.children['primary']?.segments.map((segment) => segment.path).join('/') !== targetPath.slice(1)) {
      return false;
    }

    const currentConfigPath = this.router.parseUrl(this.router.url).queryParams?.['config_path'];
    if (item.config_path && currentConfigPath !== item.config_path) return false;

    return true;
  }

  public isMenuItemClickOnly(item: SideNavItem): boolean {
    return item?.clickable === true;
  }

  public isMenuActive(item: SideNavItem): boolean {
    if (!item) return false;
    if (this.isActive(item)) return true;
    return this.getMenuChildren(item).some((child) => this.isActive(child));
  }

  public getUserInitials(): string {
    const fallback = 'AP';
    const source = (this.username || '').trim();
    if (!source) return fallback;
    const parts = source.split(/\s+/).filter(Boolean);
    const initials = parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : source.slice(0, 2);
    return initials.toUpperCase();
  }

  public toggleSidebar(): void {
    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    this.scheduleDesktopReflow();
  }

  /**
   * Heavy dashboards (like all-charts) may instantiate/defer widgets around the
   * sidebar transition window. Fire staged reflows so late-mounted widgets also
   * recalculate dimensions after expand/collapse.
   */
  protected scheduleDesktopReflow(): void {
    // Immediate pass
    this.responsiveLayoutService.triggerReflow();

    // During transition
    setTimeout(() => this.responsiveLayoutService.triggerReflow(), 180);

    // After transition end
    setTimeout(() => {
      this.responsiveLayoutService.triggerReflow();
      window.dispatchEvent(new Event('resize'));
    }, 360);
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }
  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }
  goToHelp() {
    const helpUrl = environment.HELP_URL || 'https://default.help.com';
    window.open(helpUrl, '_blank');
  }

  goToFeedback() {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) window.open(feedbackUrl, '_blank');
  }

  showNotifications() {
    alert('Notifications clicked!');
  }

  openThemeSelector(): void {
    // Re-clicking the Theme Options button while the dialog is open used to
    // stack a second instance on top of the first. Guard against that by
    // tracking the live ref and bailing out if it's still open.
    if (this.themeSelectorDialogRef) {
      this.themeSelectorDialogRef.addPanelClass('dvm-theme-selector-pulse');
      return;
    }
    this.themeSelectorDialogRef = this.dialog.open(ThemeSelectorComponent, {
      panelClass: 'dvm-theme-selector-panel',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
    });
    this.themeSelectorDialogRef.afterClosed().subscribe(() => {
      this.themeSelectorDialogRef = null;
    });
  }

  public toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  public isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  public isActive(item: SideNavItem): boolean {
    try {
      if (!item || !item.path) return false;
      return this.router.url.includes(item.path);
    } catch (e) {
      return false;
    }
  }

  canShowMacroShortcut(): boolean {
    return true;
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  selectMacro(macro: string): void {
    // Minimal selection handler — close panel and store selection
    this.isMacroPanelOpen = false;
    try {
      localStorage.setItem('selectedMacro', macro);
    } catch (e) {
      // ignore
    }
    console.log('Macro selected:', macro);
  }

  public hasChildren(item: SideNavItem): boolean {
    return this.getMenuChildren(item).length > 0;
  }

  public getMenuChildren(item: SideNavItem): SideNavItem[] {
    return Array.isArray(item.children) ? item.children : [];
  }
}

